/**
 * The design's chart primitives as React components, drawn from the shared geometry in
 * @aithora/shared (viz.ts) with the design's exact marks, strokes and colours:
 * icon() L33905, trendChart L33911, lineChart L33938, convBars L18899, distStrip L18926,
 * cogRing L33317, heat/heatA L33407, and the count-up of benchEnter L33424.
 * Every one draws what it is given; none makes up a value.
 */
import { useEffect, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import {
  COUNT_UP_MS,
  cogRingGeometry,
  convBarsGeometry,
  countUpFrame,
  distStripGeometry,
  heat,
  heatA,
  lineChartLayout,
  trendGeometry,
  type LineChartMarker,
  type LineSeries,
} from '@aithora/shared';

const FONT = 'Archivo, system-ui, sans-serif';

// ── icon() ──────────────────────────────────────────────────────────────────
/** A stroked 24px-grid icon from path data (design icon(paths, size)). Decorative. */
export function Icon({ paths, size = 16 }: { paths: readonly string[]; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}

/** Every icon() the design draws (Performance metric groups, L34918). */
export const DESIGN_ICONS = {
  quality: ['M12 3.2 14.4 8l5.3.8-3.9 3.7.9 5.3-4.7-2.5-4.7 2.5.9-5.3L4.3 8.8 9.6 8Z'],
  staffing: ['M9 11.4a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z', 'M2.6 19.4a6.4 6.4 0 0 1 12.8 0', 'M16.4 5.4a3.2 3.2 0 0 1 0 6.2', 'M17.6 13.6a5.4 5.4 0 0 1 3.8 5.8'],
  inspection: ['M6 3.4h9.6L20 7.8v12.8H6Z', 'M15.2 3.4v4.6H20', 'M9 12.6h7', 'M9 16.4h4.4'],
  cash: ['M3 7.6h18v10.2H3Z', 'M3 11h18', 'M6.6 15h3.4'],
} as const satisfies Record<string, readonly string[]>;

// ── trendChart ──────────────────────────────────────────────────────────────
export function TrendChart({ pts, endColor, label }: { pts: readonly number[]; endColor?: string; label: string }) {
  const g = trendGeometry(pts);
  if (!g) return null;
  return (
    <svg width="100%" height={g.height} viewBox={`0 0 ${g.width} ${g.height}`} preserveAspectRatio="none" role="img" aria-label={label}>
      <path d={g.area} fill="rgba(47,92,151,.1)" />
      <path d={g.line} fill="none" stroke="#2F5C97" strokeWidth={2} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <circle r={3.4} fill={endColor || '#2F5C97'} cx={g.end.cx} cy={g.end.cy} />
    </svg>
  );
}

// ── convBars ────────────────────────────────────────────────────────────────
export function ConvBars({ vals, fill = false, label }: { vals: readonly number[]; fill?: boolean; label: string }) {
  const g = convBarsGeometry(vals);
  return (
    <svg
      width="100%"
      height={fill ? '100%' : g.height}
      viewBox={`0 0 ${g.width} ${g.height}`}
      preserveAspectRatio="none"
      style={{ display: 'block', width: '100%', height: fill ? '100%' : g.height }}
      role="img"
      aria-label={label}
    >
      <line x1={0} x2={g.width} y1={g.medianY} y2={g.medianY} stroke="rgba(34,39,46,.3)" strokeWidth={1} strokeDasharray="4 4" />
      {g.bars.map((b, i) => (
        <rect key={i} x={b.x} y={b.y} width={b.width} height={b.height} rx={2} fill={b.fill} />
      ))}
    </svg>
  );
}

// ── distStrip ───────────────────────────────────────────────────────────────
export function DistStrip({ vals, highlightIdx = null, label }: { vals: readonly (number | null)[]; highlightIdx?: number | null; label: string }) {
  const g = distStripGeometry(vals, highlightIdx);
  return (
    <svg width="100%" height={g.height} viewBox={`0 0 ${g.width} ${g.height}`} preserveAspectRatio="none" role="img" aria-label={label}>
      <rect x={g.track.x} y={g.track.y} width={g.track.width} height={g.track.height} rx={3.5} fill="rgba(34,39,46,.05)" />
      <rect x={g.band.x} y={g.band.y} width={g.band.width} height={g.band.height} rx={3.5} fill="rgba(47,92,151,.11)" />
      <line x1={g.median.x} x2={g.median.x} y1={g.median.y1} y2={g.median.y2} stroke="rgba(34,39,46,.3)" strokeWidth={1} />
      {g.dots.map((d) => (
        <circle key={d.key} cx={d.cx} cy={d.cy} r={d.r} fill={d.fill} stroke="#FCFAF4" strokeWidth={d.strokeWidth} opacity={d.opacity} />
      ))}
    </svg>
  );
}

// ── cogRing ─────────────────────────────────────────────────────────────────
export function CogRing({ score, outOf }: { score: number; outOf: number }) {
  const g = cogRingGeometry(score, outOf);
  return (
    <svg width={78} height={78} viewBox="0 0 78 78" style={{ display: 'block' }} role="img" aria-label={`${score} out of ${outOf}`}>
      <circle cx={39} cy={39} r={g.r} fill="none" stroke="rgba(34,39,46,.1)" strokeWidth={4} />
      <circle
        cx={39}
        cy={39}
        r={g.r}
        fill="none"
        stroke="#1F6B52"
        strokeWidth={4}
        strokeLinecap="round"
        strokeDasharray={g.circumference}
        strokeDashoffset={g.dashOffset}
        transform="rotate(-90 39 39)"
      />
      <text x={39} y={41} textAnchor="middle" fontSize={19} fontWeight={800} fill="#22272E">
        {String(score)}
      </text>
      <text x={39} y={54} textAnchor="middle" fontSize={10} fill="#54606C">
        {`/${outOf}`}
      </text>
    </svg>
  );
}

// ── heat / heatA cells ──────────────────────────────────────────────────────
/** A heat-map cell's colours and printed value; the page lays out the cell itself. */
export function heatCell(v: number | null, palette: 'tint' | 'portfolio' = 'tint') {
  return palette === 'portfolio' ? heatA(v) : heat(v);
}

// ── lineChart ───────────────────────────────────────────────────────────────
/**
 * Quarters across, 0 to 100 up; the portfolio envelope, a dashed peer median, change
 * markers, and a MODELED +30 day projection from the last three quarters. Hover moves a
 * crosshair; a tap pins it (tap again to unpin). `renderTip(i)` draws the tooltip for the
 * quarter under the pointer.
 */
export function LineChart({
  series,
  labels,
  peerMedian,
  markers = [],
  forecast = true,
  label,
  renderTip,
  onIndex,
}: {
  series: readonly LineSeries[];
  labels: readonly string[];
  peerMedian: number | null;
  markers?: readonly LineChartMarker[];
  forecast?: boolean;
  label: string;
  renderTip?: (i: number) => ReactNode;
  onIndex?: (i: number | null) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);
  if (labels.length < 2 || series.length === 0) return null;
  const L = lineChartLayout(series, labels, peerMedian);
  const n = labels.length;
  const anySel = series.some((s) => s.on);
  const set = (i: number | null) => {
    if (i !== hover) {
      setHover(i);
      onIndex?.(i);
    }
  };
  const indexFrom = (e: MouseEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return L.indexAt(((e.clientX - r.left) / (r.width || 1)) * L.W);
  };
  const hq = hover;
  return (
    <div style={{ position: 'relative' }}>
      <svg
        width="100%"
        height="auto"
        viewBox={`0 0 ${L.W} ${L.H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', cursor: 'crosshair' }}
        role="img"
        aria-label={label}
        onMouseMove={(e) => set(indexFrom(e))}
        onMouseLeave={() => set(pinned)}
        onClick={(e) => {
          const i = indexFrom(e);
          if (pinned === i) {
            setPinned(null);
            set(null);
          } else {
            setPinned(i);
            set(i);
          }
        }}
      >
        {L.ticks.map((g) => (
          <g key={`g${g}`}>
            <line x1={L.mL} x2={L.W - L.mR} y1={L.y(g)} y2={L.y(g)} stroke="rgba(34,39,46,.08)" strokeWidth={1} />
            <text x={L.mL - 9} y={L.y(g) + 4} textAnchor="end" fontSize={14} fill="#54606C" fontFamily={FONT}>
              {String(g)}
            </text>
          </g>
        ))}
        {L.envelope && <path d={L.envelope} fill="rgba(34,39,46,.045)" />}
        {labels.map((q, i) => (
          <text key={`q${i}`} x={L.x(i)} y={L.H - 11} textAnchor="middle" fontSize={14} fill={hq === i ? '#22272E' : '#54606C'} fontWeight={hq === i ? 700 : 400} fontFamily={FONT}>
            {q}
          </text>
        ))}
        {markers.map((m) => (
          <g key={`m${m.at}`}>
            <line x1={L.x(m.at)} x2={L.x(m.at)} y1={L.mT} y2={L.H - L.mB} stroke="rgba(34,39,46,.22)" strokeWidth={1} strokeDasharray="2 4" />
            <text x={L.x(m.at) - 5} textAnchor="end" y={L.mT - 9} fontSize={14} fill="#54606C" fontFamily={FONT}>
              {m.label}
            </text>
            <rect x={L.x(m.at) - 8} y={L.mT - 22} width={16} height={L.H - L.mB - L.mT + 22} fill="transparent" style={{ cursor: 'help' }}>
              <title>{m.note}</title>
            </rect>
          </g>
        ))}
        {peerMedian !== null && (
          <>
            <line x1={L.mL} x2={L.W - L.mR} y1={L.y(peerMedian)} y2={L.y(peerMedian)} stroke="#5B6B7C" strokeWidth={1.4} strokeDasharray="5 4" />
            <text x={L.mL + 6} y={L.y(peerMedian) - 6} fontSize={14} fill="#5B6B7C" fontWeight={600}>
              {`Peer median ${peerMedian}`}
            </text>
          </>
        )}
        {hq !== null && <line x1={L.x(hq)} x2={L.x(hq)} y1={L.mT} y2={L.H - L.mB} stroke="rgba(47,92,151,.45)" strokeWidth={1.5} />}
        {forecast && (
          <>
            <rect x={L.x(n - 1)} y={L.mT} width={L.forecastX - L.x(n - 1) + 14} height={L.H - L.mT - L.mB} fill="rgba(47,92,151,.05)" />
            <text x={L.W - 2} y={L.mT - 9} textAnchor="end" fontSize={13} fill="#264C7E" fontWeight={700} letterSpacing=".06em" fontFamily={FONT}>
              +30d MODELED
            </text>
            <text x={L.forecastX} y={L.H - 11} textAnchor="middle" fontSize={14} fill="#54606C" fontFamily={FONT}>
              +30d
            </text>
            {series.map((s, si) => {
              const dimF = anySel && !s.on;
              return (
                <g key={`f${s.key}`}>
                  <path
                    d={`M${L.x(n - 1).toFixed(1)} ${L.y(s.pts[n - 1]).toFixed(1)} L${L.forecastX.toFixed(1)} ${L.y(L.forecast[si]).toFixed(1)}`}
                    fill="none"
                    stroke={dimF ? 'rgba(34,39,46,.14)' : s.color}
                    strokeWidth={s.on ? 2.4 : 1.5}
                    strokeDasharray="3 4"
                    strokeLinecap="round"
                  />
                  <circle cx={L.forecastX} cy={L.y(L.forecast[si])} r={s.on ? 4.4 : 3} fill="#FCFAF4" stroke={dimF ? 'rgba(34,39,46,.2)' : s.color} strokeWidth={1.6} />
                </g>
              );
            })}
          </>
        )}
        {series.map((s, si) => {
          const dim = anySel && !s.on;
          return (
            <g key={`l${s.key}`}>
              <path
                d={L.paths[si]}
                fill="none"
                strokeLinejoin="round"
                strokeLinecap="round"
                stroke={dim ? 'rgba(34,39,46,.16)' : s.color}
                strokeWidth={s.on ? 3.8 : 2.6}
                pathLength={1}
                strokeDasharray={1}
                style={{ animation: `drawLine 1.1s cubic-bezier(.22,.61,.36,1) ${si * 70}ms both` }}
              />
              <circle cx={L.x(n - 1)} cy={L.y(s.pts[n - 1])} r={s.on ? 4.6 : 3} fill={dim ? 'rgba(34,39,46,.2)' : s.color} />
              {hq !== null && !dim && <circle cx={L.x(hq)} cy={L.y(s.pts[hq])} r={s.on ? 5 : 3.6} fill={s.color} stroke="#FCFAF4" strokeWidth={1.5} />}
            </g>
          );
        })}
      </svg>
      {hq !== null && renderTip && (
        <div role="status" className="chart-tip" style={{ left: `${(L.x(hq) / L.W) * 100}%` }}>
          {renderTip(hq)}
        </div>
      )}
    </div>
  );
}

// ── Count-up ────────────────────────────────────────────────────────────────
function reducedMotion(): boolean {
  try {
    return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * The printed text, counting up to itself once when it first shows and again whenever
 * the value changes (design benchEnter: 850ms, cubic ease-out, lands exactly on the
 * string). Reduced motion shows the value at once.
 */
export function useCountUp(text: string): string {
  const [shown, setShown] = useState(text);
  const last = useRef<string | null>(null);
  useEffect(() => {
    if (last.current === text) return;
    last.current = text;
    if (reducedMotion() || countUpFrame(text, 0) === null || typeof requestAnimationFrame === 'undefined') {
      setShown(text);
      return;
    }
    const t0 = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / COUNT_UP_MS);
      setShown(countUpFrame(text, p) ?? text);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text]);
  return shown;
}

/** A figure that counts up to its printed value. Screen readers get the final value. */
export function CountUp({ text, className, style }: { text: string; className?: string; style?: CSSProperties }) {
  const shown = useCountUp(text);
  return (
    <span className={className} style={style} aria-label={text}>
      <span aria-hidden="true">{shown}</span>
    </span>
  );
}
